/**
 * Types for scheduled analysis and Quick Launch
 */

export enum ScheduleType {
  ONCE = 'once',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

export enum DateRangeType {
  LAST_3_DAYS = 'last_3_days',
  LAST_7_DAYS = 'last_7_days',
  LAST_14_DAYS = 'last_14_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_60_DAYS = 'last_60_days',
  LAST_90_DAYS = 'last_90_days',
  CUSTOM = 'custom'
}

export enum AnalysisMode {
  RECURRING = 'recurring',
  QUICK_LAUNCH = 'quick_launch'
}

export enum ProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  READY = 'ready',
  FAILED = 'failed',
  SKIPPED = 'skipped'
}

export enum CoTravelerType {
  CO_LOCATION = 'CO_LOCATION',
  CO_TRAVEL = 'CO_TRAVEL',
  FOLLOWER = 'FOLLOWER'
}

export interface ScheduledAnalysisConfig {
  students: string[];
  device_type: string;
  analysis_type: string;
  parameters: Record<string, any>;
  course_id?: string;
  processing_mode?: 'single' | 'individual';  // Single report or individual per entity
}

export interface ScheduledAnalysis {
  id: number;
  name: string;
  description?: string;
  user_id: string;
  analysis_config: ScheduledAnalysisConfig;
  schedule_type: ScheduleType;
  schedule_time: string;  // "14:30:00" format
  schedule_days?: string;  // "MON,WED,FRI"
  timezone: string;
  date_range_type: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  run_count: number;
  last_job_id?: number;
  last_error?: string;
  consecutive_failures: number;
  created_at: string;
  updated_at?: string;
}

export interface ScheduledAnalysisCreate {
  name: string;
  description?: string;
  analysis_config: ScheduledAnalysisConfig;
  schedule_type: ScheduleType;
  schedule_time: string;
  schedule_days?: string;
  timezone?: string;
  date_range_type: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active?: boolean;
}

export interface ScheduledAnalysisUpdate {
  name?: string;
  description?: string;
  analysis_config?: ScheduledAnalysisConfig;
  schedule_type?: ScheduleType;
  schedule_time?: string;
  schedule_days?: string;
  timezone?: string;
  date_range_type?: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active?: boolean;
}

// ========== QUICK LAUNCH TYPES ==========

export interface QuickLaunchCoTraveler {
  id: number;
  student_id: string;
  cotraveler_id: string;
  correlation_type: string;
  encounter_count: number;
  confidence_score: number;
  encounter_locations?: Array<{
    lat: number;
    lon: number;
    timestamp?: string;
    duration_minutes?: number;
  }>;
  encounter_times?: string[];
  avg_distance_meters?: number;
  created_at: string;
}

// Co-traveler filter type (matches Pathfinder's load dropdown)
export type CoTravelerFilterType = 'all' | 'students' | 'rp_srp';

export interface QuickLaunchCreate {
  name: string;
  description?: string;
  course_id: string;
  students: string[];
  exercise_date: string;  // YYYY-MM-DD
  exercise_start_time: string;  // HH:MM:SS
  exercise_end_time: string;  // HH:MM:SS
  include_cotravelers: boolean;
  cotraveler_filter?: CoTravelerFilterType;  // Default filter when loading (matches Pathfinder)
  repeat_daily?: boolean;  // Auto-update date and re-run analysis each day
  schedule_time?: string;  // HH:MM:SS - Time when daily analysis runs
  timezone?: string;  // IANA tz (e.g. "America/New_York"); BE uses this to convert exercise window
}

export interface QuickLaunchUpdate {
  name?: string;
  description?: string;
  course_id?: string;
  students?: string[];
  exercise_date?: string;  // YYYY-MM-DD
  exercise_start_time?: string;  // HH:MM:SS
  exercise_end_time?: string;  // HH:MM:SS
  include_cotravelers?: boolean;
  cotraveler_filter?: CoTravelerFilterType;
  repeat_daily?: boolean;
  schedule_time?: string;  // HH:MM:SS
  timezone?: string;
}

export interface QuickLaunch {
  id: number;
  name: string;
  description?: string;
  user_id: string;
  course_id?: string;
  students: string[];
  exercise_date?: string;
  exercise_start_time?: string;
  exercise_end_time?: string;
  include_cotravelers: boolean;
  cotraveler_filter?: CoTravelerFilterType;  // Default filter when loading (matches Pathfinder)
  repeat_daily?: boolean;  // Auto-update date and re-run analysis each day
  schedule_time?: string;  // HH:MM:SS - Time when daily analysis runs
  timezone?: string;  // IANA tz for exercise_date/start/end wall-clock intent
  processing_status: ProcessingStatus;
  processing_started_at?: string;
  processing_completed_at?: string;
  created_at: string;
  updated_at?: string;
  cotravelers?: QuickLaunchCoTraveler[];
}

export interface QuickLaunchStudentButton {
  student_id: string;
  cotraveler_count: number;
  cotravelers: string[];
  pathfinder_url: string;
  point_count?: number | null;  // Number of GPS points found (null = not yet computed, 0 = no data)
}

export interface QuickLaunchButtons {
  quick_launch_id: number;
  name: string;
  course_id: string;
  exercise_date: string;
  exercise_start_time: string;
  exercise_end_time: string;
  timezone?: string;
  start_utc?: string;
  end_utc?: string;
  processing_status: ProcessingStatus;
  students: QuickLaunchStudentButton[];
}

